// CheckoutPage.jsx — drop into src/pages/
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../hooks/AuthContext";
import config from "../config";

const { API_BASE } = config;

/* ── VAT ── prices stored/quoted throughout the shop are EXCLUSIVE of VAT;
   it's calculated here and added as its own line at checkout. Must match
   the rate applied server-side in checkout.php. ── */
const VAT_RATE = 0.15;

function formatPrice(val) {
  if (val === null || val === undefined) return "R0.00";
  return "R" + Number(val).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* ── Steps ── */
const STEPS = [
  { id: "details",  label: "Details"  },
  { id: "payment",  label: "Payment"  },
  { id: "confirm",  label: "Confirm"  },
];

/* ── Reusable field ── */
function Field({ label, type = "text", value, onChange, placeholder, required, hint, disabled, rows }) {
  return (
    <div className="ck-field">
      <label className="ck-field__label">
        {label}{required && <span className="ck-field__req"> *</span>}
      </label>
      {rows ? (
        <textarea
          className="ck-field__input ck-field__textarea"
          value={value}
          onChange={onChange ? (e) => onChange(e.target.value) : undefined}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
        />
      ) : (
        <input
          className={`ck-field__input${disabled ? " ck-field__input--disabled" : ""}`}
          type={type}
          value={value}
          onChange={onChange ? (e) => onChange(e.target.value) : undefined}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
        />
      )}
      {hint && <p className="ck-field__hint">{hint}</p>}
    </div>
  );
}

/* ── Step indicator ── */
function StepBar({ current }) {
  const idx = STEPS.findIndex(s => s.id === current);
  return (
    <div className="ck-steps" aria-label="Checkout progress">
      {STEPS.map((step, i) => {
        const done   = i < idx;
        const active = i === idx;
        return (
          <div key={step.id} className="ck-steps__item">
            <div className={`ck-steps__dot ${done ? "ck-steps__dot--done" : ""} ${active ? "ck-steps__dot--active" : ""}`}>
              {done ? (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
                  <path d="M2 6l3 3 5-5"/>
                </svg>
              ) : (
                <span>{i + 1}</span>
              )}
            </div>
            <span className={`ck-steps__label ${active ? "ck-steps__label--active" : ""} ${done ? "ck-steps__label--done" : ""}`}>
              {step.label}
            </span>
            {i < STEPS.length - 1 && (
              <div className={`ck-steps__line ${done ? "ck-steps__line--done" : ""}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Cart summary sidebar ── */
function OrderSummary({ cartItems, subtotal, discount, vat, shipping, coupon, couponInput, setCouponInput, onApplyCoupon, couponMsg, total }) {
  return (
    <div className="ck-summary">
      <h2 className="ck-summary__title">Order Summary</h2>
      <div className="ck-summary__items">
        {cartItems.map(item => {
          const price     = Number(item.sale_price || item.price) || 0;
          const lineTotal = price * item.qty;
          return (
            <div key={item.id} className="ck-sum-item">
              <div className="ck-sum-item__img-wrap">
                {item.image1
                  ? <img src={`${config.SHOP_UPLOADS_URL}/${item.image1}`} alt={item.name} className="ck-sum-item__img" />
                  : <div className="ck-sum-item__img-fallback"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/></svg></div>}
                <span className="ck-sum-item__qty">{item.qty}</span>
              </div>
              <div className="ck-sum-item__info">
                <span className="ck-sum-item__name">{item.name}</span>
                {item.brand && <span className="ck-sum-item__brand">{item.brand}</span>}
              </div>
              <span className="ck-sum-item__total">{formatPrice(lineTotal)}</span>
            </div>
          );
        })}
      </div>

      {/* Coupon */}
      <div className="ck-coupon">
        <div className="ck-coupon__row">
          <input
            className="ck-coupon__input"
            type="text"
            placeholder="Discount code"
            value={couponInput}
            onChange={e => setCouponInput(e.target.value.toUpperCase())}
          />
          <button className="ck-coupon__btn" onClick={onApplyCoupon}>Apply</button>
        </div>
        {couponMsg && (
          <p className={`ck-coupon__msg ck-coupon__msg--${couponMsg.type}`}>{couponMsg.text}</p>
        )}
      </div>

      {/* Totals */}
      <div className="ck-totals">
        <div className="ck-totals__row">
          <span>Subtotal</span>
          <span>{formatPrice(subtotal)}</span>
        </div>
        {discount > 0 && (
          <div className="ck-totals__row ck-totals__row--discount">
            <span>Discount {coupon && `(${coupon})`}</span>
            <span>−{formatPrice(discount)}</span>
          </div>
        )}
        <div className="ck-totals__row">
          <span>VAT (15%)</span>
          <span>{formatPrice(vat)}</span>
        </div>
        <div className="ck-totals__row">
          <span>Shipping</span>
          <span className={shipping === 0 ? "ck-totals__free" : ""}>{shipping === 0 ? "Calculated on delivery" : formatPrice(shipping)}</span>
        </div>
        <div className="ck-totals__divider" />
        <div className="ck-totals__row ck-totals__row--total">
          <span>Total</span>
          <span>{formatPrice(total)}</span>
        </div>
        <p className="ck-totals__vat">VAT is calculated on subtotal minus discount</p>
      </div>
    </div>
  );
}

/* ── EFT Banking card ── */
function BankingCard({ bank }) {
  const [copied, setCopied] = useState(null);
  function copy(val, key) {
    navigator.clipboard?.writeText(val).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  }
  const rows = [
    { label: "Bank",           key: "bank_name",      val: bank.bank_name },
    { label: "Account name",   key: "account_name",   val: bank.account_name },
    { label: "Account number", key: "account_number", val: bank.account_number },
    { label: "Branch code",    key: "branch_code",    val: bank.branch_code },
    { label: "Account type",   key: "account_type",   val: bank.account_type },
  ];
  return (
    <div className="ck-bank-card">
      <div className="ck-bank-card__header">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/>
        </svg>
        {bank.bank_name}
      </div>
      <div className="ck-bank-card__rows">
        {rows.map(r => r.val ? (
          <div key={r.key} className="ck-bank-card__row">
            <span className="ck-bank-card__label">{r.label}</span>
            <div className="ck-bank-card__val-wrap">
              <span className="ck-bank-card__val">{r.val}</span>
              <button className="ck-bank-card__copy" onClick={() => copy(r.val, r.key)} aria-label={`Copy ${r.label}`}>
                {copied === r.key ? (
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round"><path d="M2 6.5l3 3 6-6"/></svg>
                ) : (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                )}
              </button>
            </div>
          </div>
        ) : null)}
      </div>
      {bank.reference_note && (
        <div className="ck-bank-card__ref">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
          </svg>
          <span><strong>Payment reference:</strong> {bank.reference_note}</span>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════
   MAIN CheckoutPage
════════════════════════════════════════════ */
export default function CheckoutPage({ cartItems = [], onClearCart }) {
  const { token, customer, isLoggedIn } = useAuthContext();
  const navigate = useNavigate();

  const [orderPlaced, setOrderPlaced] = useState(false);

  /* Redirect if not logged in or cart is empty */
  useEffect(() => {
    if (!isLoggedIn) { navigate("/shop"); return; }
    if (cartItems.length === 0 && !orderPlaced) { navigate("/shop"); }
    }, [isLoggedIn, cartItems.length, orderPlaced, navigate]);

  /* ── State ── */
  const [step, setStep] = useState("details");

  // Details form — pre-fill from customer
  const [details, setDetails] = useState({
    first_name:  customer?.first_name  || "",
    last_name:   customer?.last_name   || "",
    email:       customer?.email       || "",
    phone:       customer?.phone       || "",
    address:     customer?.address     || "",
    city:        customer?.city        || "",
    province:    customer?.province    || "",
    postal_code: customer?.postal_code || "",
    notes:       "",
  });
  const setDetail = k => v => setDetails(d => ({ ...d, [k]: v }));

  // Payment
  const [paymentMethod, setPaymentMethod] = useState("eft");

  // Coupon
  const [couponInput, setCouponInput] = useState("");
  const [coupon,      setCoupon]      = useState(null);
  const [couponMsg,   setCouponMsg]   = useState(null);
  const [discount,    setDiscount]    = useState(0);

  // Checkout data from backend
  const [banking,   setBanking]  = useState([]);
  const [contact,   setContact]  = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [order, setOrder] = useState(null); 

  // Totals — product prices are VAT-exclusive; VAT is added on (subtotal − discount)
  const subtotal = cartItems.reduce((sum, item) => {
    return sum + Number(item.sale_price || item.price || 0) * item.qty;
  }, 0);
  const shipping = 0; // calculated on delivery
  const vat      = Math.round((subtotal - discount) * VAT_RATE * 100) / 100;
  const total    = Math.max(0, subtotal - discount + vat + shipping);

  /* Fetch banking + contact on mount */
  useEffect(() => {
    fetch(`${API_BASE}/checkout.php`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.json())
      .then(data => {
        setBanking(data.banking || []);
        setContact(data.contact || null);
      })
      .catch(() => {});
  }, [token]);

  /* Apply coupon */
  const handleApplyCoupon = useCallback(async () => {
    if (!couponInput.trim()) return;
    setCouponMsg(null);
    try {
      const res  = await fetch(`${API_BASE}/apply-coupon.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code: couponInput.trim(), subtotal }),
      });
      const data = await res.json();
      if (!res.ok) { setCouponMsg({ type: "error", text: data.error || "Invalid code." }); return; }
      setDiscount(data.discount_amount || 0);
      setCoupon(couponInput.trim());
      setCouponMsg({ type: "success", text: `${data.description || "Coupon applied"}!` });
    } catch {
      setCouponMsg({ type: "error", text: "Could not apply coupon." });
    }
  }, [couponInput, subtotal, token]);

  /* Validate details step */
  function validateDetails() {
    const required = ["first_name","last_name","email","phone","address","city","province","postal_code"];
    for (const k of required) {
      if (!details[k]?.trim()) return `Please fill in: ${k.replace(/_/g," ")}`;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email)) return "Please enter a valid email address.";
    return null;
  }

  function handleDetailsNext() {
    const err = validateDetails();
    if (err) { setSubmitError(err); return; }
    setSubmitError(null);
    setStep("payment");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handlePaymentNext() {
    setStep("confirm");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  /* Place order */
  async function handlePlaceOrder() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body = {
        customer_name:    `${details.first_name} ${details.last_name}`.trim(),
        customer_email:   details.email,
        customer_phone:   details.phone,
        shipping_address: `${details.address}, ${details.city}, ${details.province}, ${details.postal_code}`,
        notes:            details.notes,
        payment_method:   paymentMethod,
        coupon_code:      coupon || "",
        shipping_cost:    shipping,
        // FIX: use the real product_id + btu_option_id from the cart item, not the
        // cart-line's own `id` (which for BTU variants is a synthetic string like
        // "123-btu7" used only to keep different sizes as separate cart lines).
        items: cartItems.map(item => ({
          product_id: item.product_id || item.id,
          option_id:  item.option_id || item.btu_option_id || null,
          name:       item.name,
          sku:        item.sku || "",
          qty:        item.qty,
          price:      Number(item.sale_price || item.price) || 0,
        })),
      };

      const res  = await fetch(`${API_BASE}/checkout.php`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body:    JSON.stringify(body),
      });
      const data = await res.json();

      if (!res.ok) { setSubmitError(data.error || "Could not place order."); return; }

      setOrder(data);
      setOrderPlaced(true);
      onClearCart?.();
      setStep("done");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setSubmitError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  /* ── Render: confirmation screen ── */
  if (step === "done" && order) {
    const bankList  = order.banking  || banking;
    const contactInfo = order.contact || contact;
    const isEFT     = order.payment_method === "eft";
    const whatsapp  = contactInfo?.phone_number?.replace(/\D/g, "");
    const waLink    = whatsapp
      ? `https://wa.me/${whatsapp.startsWith("0") ? "27" + whatsapp.slice(1) : whatsapp}?text=${encodeURIComponent(`Hi, I'd like to send proof of payment for order ${order.order_number} (${formatPrice(order.total)}).`)}`
      : null;

    return (
      <div className="ck-page">
        <div className="ck-hero">
          <div className="ck-hero__inner">
            <div className="ck-done__icon" aria-hidden="true">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
                <path d="M20 6L9 17l-5-5"/>
              </svg>
            </div>
            <div>
              <h1 className="ck-hero__title">Order placed!</h1>
              <p className="ck-hero__sub">Order <strong>{order.order_number}</strong></p>
            </div>
          </div>
        </div>

        <div className="ck-done">
          <div className="ck-done__main">

            {/* What happens next */}
            <div className="ck-card">
              <h2 className="ck-card__title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#013d97" strokeWidth="2" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
                </svg>
                What happens next
              </h2>
              <ol className="ck-steps-list">
                {isEFT ? (
                  <>
                    <li><strong>Make your EFT payment</strong> using the banking details below.</li>
                    <li><strong>Use your order number</strong> <code className="ck-code">{order.order_number}</code> as the payment reference.</li>
                    <li><strong>Send your proof of payment</strong> via WhatsApp or email (details below).</li>
                    <li>We'll confirm your order and arrange delivery once payment is verified — usually within 1 business day.</li>
                  </>
                ) : (
                  <>
                    <li><strong>Your order has been received.</strong> We'll contact you to confirm.</li>
                    <li>Payment of <strong>{formatPrice(order.total)}</strong> is due <strong>on delivery/collection</strong> in cash.</li>
                    <li>Please have the exact amount ready — we appreciate it.</li>
                    <li>You'll receive delivery details from our team shortly.</li>
                  </>
                )}
              </ol>
            </div>

            {/* EFT banking details */}
            {isEFT && bankList.length > 0 && (
              <div className="ck-card">
                <h2 className="ck-card__title">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#013d97" strokeWidth="2" aria-hidden="true">
                    <rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/>
                  </svg>
                  EFT Banking Details
                </h2>
                <div className="ck-alert ck-alert--blue">
                  Use <strong>{order.order_number}</strong> as your payment reference so we can match your payment instantly.
                </div>
                {bankList.map((bank, i) => <BankingCard key={i} bank={bank} />)}
              </div>
            )}

            {/* Send proof of payment */}
            {isEFT && contactInfo && (
              <div className="ck-card">
                <h2 className="ck-card__title">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#013d97" strokeWidth="2" aria-hidden="true">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 3.07 9.81a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 2 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L6.91 9.91a16 16 0 0 0 6.18 6.18l1.27-.34a2 2 0 0 1 2.11.45c.907.34 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                  Send Proof of Payment
                </h2>
                <p className="ck-card__body-text">
                  After making payment, please send your proof of payment via WhatsApp or email. Include your order number <code className="ck-code">{order.order_number}</code>.
                </p>
                <div className="ck-contact-btns">
                  {waLink && (
                    <a href={waLink} target="_blank" rel="noopener noreferrer" className="ck-contact-btn ck-contact-btn--wa">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                        <path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.554 4.103 1.523 5.824L.057 23.928a.5.5 0 0 0 .6.633l6.313-1.434A11.94 11.94 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 0 1-5.001-1.369l-.358-.214-3.715.844.877-3.609-.234-.371A9.818 9.818 0 1 1 12 21.818z"/>
                      </svg>
                      WhatsApp {contactInfo.phone_number}
                    </a>
                  )}
                  {contactInfo.email_address && (
                    <a
                      href={`mailto:${contactInfo.email_address}?subject=Proof of Payment – ${order.order_number}&body=Hi, please find attached proof of payment for order ${order.order_number} (${formatPrice(order.total)}).`}
                      className="ck-contact-btn ck-contact-btn--email"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                      Email {contactInfo.email_address}
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Cash note */}
            {!isEFT && contactInfo && (
              <div className="ck-card">
                <h2 className="ck-card__title">Questions about your order?</h2>
                <div className="ck-contact-btns">
                  {contactInfo.phone_number && (
                    <a href={`https://wa.me/${contactInfo.phone_number.replace(/\D/g,"").replace(/^0/,"27")}`} target="_blank" rel="noopener noreferrer" className="ck-contact-btn ck-contact-btn--wa">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.554 4.103 1.523 5.824L.057 23.928a.5.5 0 0 0 .6.633l6.313-1.434A11.94 11.94 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 0 1-5.001-1.369l-.358-.214-3.715.844.877-3.609-.234-.371A9.818 9.818 0 1 1 12 21.818z"/></svg>
                      WhatsApp us
                    </a>
                  )}
                  {contactInfo.email_address && (
                    <a href={`mailto:${contactInfo.email_address}`} className="ck-contact-btn ck-contact-btn--email">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                      {contactInfo.email_address}
                    </a>
                  )}
                </div>
              </div>
            )}

            <div className="ck-done__actions">
              <button className="ck-btn ck-btn--ghost" onClick={() => navigate("/account?tab=orders")}>View my orders</button>
              <button className="ck-btn ck-btn--primary" onClick={() => navigate("/shop")}>Continue shopping</button>
            </div>
          </div>
        </div>
        <style>{STYLES}</style>
      </div>
    );
  }

  /* ── Render: active checkout steps ── */
  return (
    <div className="ck-page">
      {/* Hero */}
      <div className="ck-hero">
        <div className="ck-hero__inner">
          <div>
            <nav className="ck-bread" aria-label="Breadcrumb">
              <a href="/" className="ck-bread__link">Home</a>
              <span aria-hidden="true">›</span>
              <a href="/shop" className="ck-bread__link">Shop</a>
              <span aria-hidden="true">›</span>
              <span>Checkout</span>
            </nav>
            <h1 className="ck-hero__title">Checkout</h1>
          </div>
        </div>
      </div>

      <div className="ck-layout">
        {/* Left: form */}
        <div className="ck-main">
          <StepBar current={step} />

          {submitError && (
            <div className="ck-alert ck-alert--error">{submitError}</div>
          )}

          {/* ── Step: Details ── */}
          {step === "details" && (
            <div className="ck-card">
              <h2 className="ck-card__title">Delivery Information</h2>
              <div className="ck-grid2">
                <Field label="First name"  value={details.first_name}  onChange={setDetail("first_name")}  required placeholder="Jane" />
                <Field label="Last name"   value={details.last_name}   onChange={setDetail("last_name")}   required placeholder="Smith" />
              </div>
              <Field label="Email address" type="email" value={details.email} onChange={setDetail("email")} required placeholder="jane@example.com" />
              <Field label="Phone number"  type="tel"   value={details.phone} onChange={setDetail("phone")} required placeholder="+27 82 000 0000" />
              <div className="ck-divider"><span>Delivery address</span></div>
              <Field label="Street address" value={details.address}     onChange={setDetail("address")}     required placeholder="12 Example Street" />
              <div className="ck-grid2">
                <Field label="City"     value={details.city}     onChange={setDetail("city")}     required placeholder="Cape Town" />
                <Field label="Province" value={details.province} onChange={setDetail("province")} required placeholder="Western Cape" />
              </div>
              <Field label="Postal code" value={details.postal_code} onChange={setDetail("postal_code")} required placeholder="8001" />
              <Field label="Order notes (optional)" value={details.notes} onChange={setDetail("notes")} placeholder="Special instructions or access notes…" rows={3} />
              <div className="ck-card__actions">
                <button className="ck-btn ck-btn--primary ck-btn--full" onClick={handleDetailsNext}>
                  Continue to payment
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </button>
              </div>
            </div>
          )}

          {/* ── Step: Payment ── */}
          {step === "payment" && (
            <div className="ck-card">
              <h2 className="ck-card__title">Payment Method</h2>
              <p className="ck-card__body-text">Choose how you'd like to pay. All orders are confirmed once payment is received.</p>

              <div className="ck-pay-opts">
                {/* EFT */}
                <label className={`ck-pay-opt ${paymentMethod === "eft" ? "ck-pay-opt--selected" : ""}`}>
                  <input type="radio" name="payment" value="eft" checked={paymentMethod === "eft"} onChange={() => setPaymentMethod("eft")} className="ck-pay-opt__radio" />
                  <div className="ck-pay-opt__icon ck-pay-opt__icon--eft" aria-hidden="true">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/>
                    </svg>
                  </div>
                  <div className="ck-pay-opt__body">
                    <span className="ck-pay-opt__name">EFT / Bank Transfer</span>
                    <span className="ck-pay-opt__desc">Pay directly into our bank account. Banking details will be provided after placing your order.</span>
                  </div>
                  <div className={`ck-pay-opt__check ${paymentMethod === "eft" ? "ck-pay-opt__check--on" : ""}`}>
                    {paymentMethod === "eft" && (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><path d="M2 6l3 3 5-5"/></svg>
                    )}
                  </div>
                </label>

                {/* Cash */}
                <label className={`ck-pay-opt ${paymentMethod === "cash" ? "ck-pay-opt--selected" : ""}`}>
                  <input type="radio" name="payment" value="cash" checked={paymentMethod === "cash"} onChange={() => setPaymentMethod("cash")} className="ck-pay-opt__radio" />
                  <div className="ck-pay-opt__icon ck-pay-opt__icon--cash" aria-hidden="true">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <rect x="2" y="6" width="20" height="12" rx="2"/>
                      <circle cx="12" cy="12" r="3"/>
                      <path d="M6 12h.01M18 12h.01"/>
                    </svg>
                  </div>
                  <div className="ck-pay-opt__body">
                    <span className="ck-pay-opt__name">Cash on Delivery / Collection</span>
                    <span className="ck-pay-opt__desc">Pay in cash when your order is delivered or collected. Exact change appreciated.</span>
                  </div>
                  <div className={`ck-pay-opt__check ${paymentMethod === "cash" ? "ck-pay-opt__check--on" : ""}`}>
                    {paymentMethod === "cash" && (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><path d="M2 6l3 3 5-5"/></svg>
                    )}
                  </div>
                </label>
              </div>

              {/* EFT preview */}
              {paymentMethod === "eft" && banking.length > 0 && (
                <div className="ck-eft-preview">
                  <p className="ck-eft-preview__label">Banking details — you'll also receive these after placing your order:</p>
                  {banking.map((bank, i) => <BankingCard key={i} bank={bank} />)}
                </div>
              )}

              <div className="ck-card__actions ck-card__actions--row">
                <button className="ck-btn ck-btn--ghost" onClick={() => setStep("details")}>← Back</button>
                <button className="ck-btn ck-btn--primary" onClick={handlePaymentNext}>
                  Review order
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </button>
              </div>
            </div>
          )}

          {/* ── Step: Confirm ── */}
          {step === "confirm" && (
            <div className="ck-card">
              <h2 className="ck-card__title">Review & Place Order</h2>

              {/* Delivery summary */}
              <div className="ck-review-section">
                <div className="ck-review-section__head">
                  <span>Delivery details</span>
                  <button className="ck-review-section__edit" onClick={() => setStep("details")}>Edit</button>
                </div>
                <p className="ck-review-section__val">{details.first_name} {details.last_name}</p>
                <p className="ck-review-section__val">{details.email} · {details.phone}</p>
                <p className="ck-review-section__val">{details.address}, {details.city}, {details.province} {details.postal_code}</p>
                {details.notes && <p className="ck-review-section__note">Note: {details.notes}</p>}
              </div>

              {/* Payment summary */}
              <div className="ck-review-section">
                <div className="ck-review-section__head">
                  <span>Payment method</span>
                  <button className="ck-review-section__edit" onClick={() => setStep("payment")}>Edit</button>
                </div>
                <p className="ck-review-section__val">
                  {paymentMethod === "eft" ? "EFT / Bank Transfer" : "Cash on Delivery / Collection"}
                </p>
              </div>

              {/* T&C note */}
              <div className="ck-alert ck-alert--blue">
                By placing your order you confirm that the details above are correct. Orders are only processed once payment is received{paymentMethod === "eft" ? " and proof of payment is submitted" : ""}.
              </div>

              <div className="ck-card__actions ck-card__actions--row">
                <button className="ck-btn ck-btn--ghost" onClick={() => setStep("payment")}>← Back</button>
                <button
                  className="ck-btn ck-btn--primary ck-btn--place"
                  onClick={handlePlaceOrder}
                  disabled={submitting}
                >
                  {submitting ? (
                    <><span className="ck-spinner" />Placing order…</>
                  ) : (
                    <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>Place order — {formatPrice(total)}</>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right: order summary */}
        <aside className="ck-aside">
          <OrderSummary
            cartItems={cartItems}
            subtotal={subtotal}
            discount={discount}
            vat={vat}
            shipping={shipping}
            coupon={coupon}
            couponInput={couponInput}
            setCouponInput={setCouponInput}
            onApplyCoupon={handleApplyCoupon}
            couponMsg={couponMsg}
            total={total}
          />
        </aside>
      </div>

      <style>{STYLES}</style>
    </div>
  );
}

/* ══════════════════════════════════════
   STYLES
══════════════════════════════════════ */
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;800;900&display=swap');

/* ── Reset & base ── */
.ck-page *, .ck-page *::before, .ck-page *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}
.ck-page a { text-decoration: none; }

/* FIX: prevent any child from busting out of the viewport */
.ck-page {
  font-family: 'Roboto', sans-serif;
  background: #f5f7fb;
  min-height: 100vh;
  overflow-x: hidden;   /* ← key fix: clip any accidental overflow */
  width: 100%;
}

/* ── Hero ── */
.ck-hero {
  background: linear-gradient(135deg, #011546 0%, #013d97 60%, #0255cc 100%);
  padding: 32px 0;
  position: relative;
  overflow: hidden;
}
.ck-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.04' fill-rule='evenodd'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/svg%3E");
}
.ck-hero__inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;          /* FIX: was 32px, reduced so it doesn't clip on 320px */
  display: flex;
  align-items: center;
  gap: 18px;
  position: relative;
}
.ck-bread {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: rgba(255,255,255,0.6);
  margin-bottom: 8px;
  flex-wrap: wrap;          /* FIX: wrap on very narrow screens */
}
.ck-bread__link { color: rgba(255,255,255,0.75); transition: color 0.15s; }
.ck-bread__link:hover { color: #fff; }
.ck-hero__title {
  font-size: clamp(20px, 4vw, 32px);  /* FIX: tighter clamp floor */
  font-weight: 900;
  color: #fff;
  letter-spacing: -0.02em;
}
.ck-hero__sub {
  font-size: 14px;
  color: rgba(255,255,255,0.75);
  margin-top: 4px;
}

/* Done hero icon */
.ck-done__icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(255,255,255,0.15);
  border: 2.5px solid rgba(255,255,255,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* ── Layout ── */
.ck-layout {
  max-width: 1200px;
  margin: 0 auto;
  padding: 36px 16px 80px;  /* FIX: was 32px side padding; 16px safer on mobile */
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 28px;
  align-items: start;
  width: 100%;              /* FIX: ensure it never exceeds viewport */
}
@media (max-width: 900px) {
  .ck-layout {
    grid-template-columns: 1fr;
    padding: 20px 12px 60px; /* FIX: tighter on tablet/mobile */
    gap: 20px;
  }
  .ck-aside { order: -1; }
}

/* ── Steps bar ── */
.ck-steps {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  width: 100%;
  overflow: hidden;         /* FIX: prevent step bar overflow */
}
.ck-steps__item {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;             /* FIX: allow flex children to shrink */
}
.ck-steps__item:last-child { flex: 0 0 auto; }
.ck-steps__dot {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #94a3b8;
  font-size: 13px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s, color 0.2s;
}
.ck-steps__dot--active { background: #013d97; color: #fff; }
.ck-steps__dot--done   { background: #16a34a; color: #fff; }
.ck-steps__label {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  margin-left: 8px;
  white-space: nowrap;
  overflow: hidden;         /* FIX: clip if extremely narrow */
  text-overflow: ellipsis;
  transition: color 0.2s;
}
.ck-steps__label--active { color: #013d97; font-weight: 800; }
.ck-steps__label--done   { color: #16a34a; }
.ck-steps__line {
  flex: 1;
  height: 2px;
  background: #e2e8f0;
  margin: 0 8px;            /* FIX: was 10px, a touch tighter */
  border-radius: 2px;
  min-width: 10px;          /* FIX: don't collapse to zero */
  transition: background 0.2s;
}
.ck-steps__line--done { background: #16a34a; }

/* ── Card ── */
.ck-card {
  background: #fff;
  border-radius: 14px;
  padding: 24px 20px;       /* FIX: was 28px; less horizontal squeeze on mobile */
  box-shadow: 0 1px 4px rgba(1,15,50,0.07);
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;              /* FIX: never wider than parent */
  min-width: 0;
}
.ck-card__title {
  font-size: 17px;
  font-weight: 800;
  color: #0f1c38;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;          /* FIX: wrap icon+text on narrow cards */
}
.ck-card__body-text {
  font-size: 14px;
  color: #6b7a99;
  line-height: 1.6;
}
.ck-card__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 6px;
}
.ck-card__actions--row {
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;          /* FIX: wrap Back + Continue on very small screens */
  gap: 10px;
}

/* ── Field ── */
.ck-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  min-width: 0;
}
.ck-field__label {
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.ck-field__req { color: #ef4444; }
.ck-field__input {
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 11px 14px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  color: #0f1c38;
  background: #fafbfd;
  outline: none;
  transition: border-color 0.18s, box-shadow 0.18s;
  width: 100%;
  min-width: 0;             /* FIX: inputs must not overflow their grid cell */
}
.ck-field__input:focus {
  border-color: #013d97;
  box-shadow: 0 0 0 3px rgba(1,61,151,0.1);
  background: #fff;
}
.ck-field__input--disabled {
  background: #f5f7fb;
  color: #8895b0;
  cursor: not-allowed;
}
.ck-field__textarea {
  resize: vertical;
  min-height: 80px;
}
.ck-field__hint { font-size: 12px; color: #9ca3af; }

/* ── Grid ── */
.ck-grid2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  width: 100%;
  min-width: 0;
}
@media (max-width: 480px) {
  .ck-grid2 { grid-template-columns: 1fr; }
}

/* ── Divider ── */
.ck-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #9ca3af;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.ck-divider::before, .ck-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #f0f3f8;
}

/* ── Payment options ── */
.ck-pay-opts { display: flex; flex-direction: column; gap: 12px; }
.ck-pay-opt {
  display: flex;
  align-items: flex-start;  /* FIX: was center; flex-start handles text wrap better */
  gap: 12px;                /* FIX: was 16px */
  padding: 16px;            /* FIX: was 18px 20px */
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.18s, background 0.18s;
  position: relative;
  width: 100%;
  min-width: 0;
}
.ck-pay-opt--selected {
  border-color: #013d97;
  background: #f0f5ff;
}
.ck-pay-opt__radio { position: absolute; opacity: 0; width: 0; height: 0; }
.ck-pay-opt__icon {
  width: 44px;              /* FIX: was 52px; smaller on mobile */
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ck-pay-opt__icon--eft  { background: #dbeafe; color: #1e40af; }
.ck-pay-opt__icon--cash { background: #dcfce7; color: #166534; }
.ck-pay-opt__body {
  flex: 1;
  min-width: 0;             /* FIX: allow text to shrink/wrap */
}
.ck-pay-opt__name {
  display: block;
  font-size: 14px;          /* FIX: was 15px */
  font-weight: 800;
  color: #0f1c38;
  margin-bottom: 4px;
  word-break: break-word;   /* FIX: wrap long names */
}
.ck-pay-opt__desc {
  display: block;
  font-size: 12.5px;        /* FIX: was 13px */
  color: #6b7a99;
  line-height: 1.5;
  word-break: break-word;
}
.ck-pay-opt__check {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.18s;
}
.ck-pay-opt__check--on { background: #013d97; border-color: #013d97; }

/* ── EFT preview ── */
.ck-eft-preview { display: flex; flex-direction: column; gap: 12px; }
.ck-eft-preview__label {
  font-size: 13px;
  color: #6b7a99;
  font-weight: 600;
}

/* ── Banking card ── */
.ck-bank-card {
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
  min-width: 0;
}
.ck-bank-card__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #013d97, #0255cc);
  color: #fff;
  font-size: 15px;
  font-weight: 800;
  word-break: break-word;
}
.ck-bank-card__rows { display: flex; flex-direction: column; }
.ck-bank-card__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid #f0f3f8;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;          /* FIX: wrap label+value on very narrow screens */
}
.ck-bank-card__row:last-child { border-bottom: none; }
.ck-bank-card__label {
  font-size: 11.5px;        /* FIX: slightly smaller so it fits on mobile */
  font-weight: 700;
  color: #6b7a99;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}
.ck-bank-card__val-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
  justify-content: flex-end;
}
.ck-bank-card__val {
  font-size: 13.5px;        /* FIX: was 14px */
  font-weight: 700;
  color: #0f1c38;
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.ck-bank-card__copy {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1.5px solid #e2e8f0;
  background: #f7f9fc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7a99;
  transition: all 0.15s;
  flex-shrink: 0;
}
.ck-bank-card__copy:hover { background: #e4ebf5; color: #013d97; }
.ck-bank-card__ref {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 16px;
  background: #fffbeb;
  border-top: 1px solid #fde68a;
  font-size: 13px;
  color: #92400e;
  line-height: 1.5;
  word-break: break-word;   /* FIX: wrap long reference notes */
}
.ck-bank-card__ref svg { flex-shrink: 0; margin-top: 1px; stroke: #d97706; }

/* ── Review sections ── */
.ck-review-section {
  border: 1.5px solid #f0f3f8;
  border-radius: 10px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: #fafbfd;
  min-width: 0;
  word-break: break-word;   /* FIX: long emails/addresses wrap */
}
.ck-review-section__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  font-weight: 800;
  color: #8895b0;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 6px;
  gap: 8px;
}
.ck-review-section__edit {
  background: none;
  border: none;
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #013d97;
  cursor: pointer;
  text-decoration: underline;
  flex-shrink: 0;
}
.ck-review-section__val {
  font-size: 13.5px;
  color: #0f1c38;
  font-weight: 500;
  overflow-wrap: break-word;
}
.ck-review-section__note { font-size: 13px; color: #6b7a99; font-style: italic; }

/* ── Alerts ── */
.ck-alert {
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 13.5px;
  line-height: 1.6;
  word-break: break-word;
}
.ck-alert--error  { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; }
.ck-alert--blue   { background: #eff6ff; border: 1px solid #bfdbfe; color: #1e40af; }

/* ── Steps list (confirmation) ── */
.ck-steps-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-left: 20px;
}
.ck-steps-list li {
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
  padding-left: 4px;
  overflow-wrap: break-word; /* FIX: wrap order numbers etc. */
}
.ck-steps-list li strong { color: #0f1c38; }

/* ── Code badge ── */
.ck-code {
  font-family: 'Courier New', monospace;
  background: #f0f5ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
  padding: 2px 8px;
  border-radius: 5px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  word-break: break-all;    /* FIX: very long order numbers won't overflow */
}

/* ── Contact buttons ── */
.ck-contact-btns { display: flex; flex-direction: column; gap: 10px; }
.ck-contact-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 16px;       /* FIX: was 18px right; less on small screens */
  border-radius: 10px;
  font-family: 'Roboto', sans-serif;
  font-size: 13.5px;        /* FIX: was 14px; prevents overflow on narrow */
  font-weight: 700;
  text-decoration: none;
  transition: all 0.18s;
  word-break: break-all;    /* FIX: long email addresses wrap instead of overflow */
  overflow-wrap: break-word;
}
.ck-contact-btn--wa {
  background: #25d366;
  color: #fff;
}
.ck-contact-btn--wa:hover { background: #1da851; }
.ck-contact-btn--email {
  background: #f0f4fa;
  color: #013d97;
  border: 1.5px solid #c8d4e8;
}
.ck-contact-btn--email:hover { background: #e4ebf5; }

/* ── Buttons ── */
.ck-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 10px;
  border: none;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;           /* FIX: buttons shouldn't squish below their text */
}
.ck-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.ck-btn--primary {
  background: linear-gradient(135deg, #013d97, #0255cc);
  color: #fff;
  box-shadow: 0 2px 8px rgba(1,61,151,0.25);
}
.ck-btn--primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #012f77, #0147aa);
  transform: translateY(-1px);
}
.ck-btn--ghost {
  background: #f0f4fa;
  color: #5a6a8a;
  border: 1.5px solid #e2e8f0;
}
.ck-btn--ghost:hover { background: #e4ebf5; color: #013d97; }
.ck-btn--full { width: 100%; }
.ck-btn--place { min-width: 0; } /* FIX: was 200px; let it be natural width on mobile */

.ck-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ck-spin 0.6s linear infinite;
  flex-shrink: 0;
}
@keyframes ck-spin { to { transform: rotate(360deg); } }

/* ── Order summary (sidebar) ── */
.ck-summary {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(1,15,50,0.07);
  overflow: hidden;
  position: sticky;
  top: 110px;
  width: 100%;
  min-width: 0;
}
.ck-summary__title {
  font-size: 15px;
  font-weight: 800;
  color: #0f1c38;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #f0f3f8;
}
.ck-summary__items {
  padding: 8px 0;
  max-height: 320px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #e2e8f0 transparent;
}

/* Sum item */
.ck-sum-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;       /* FIX: was 20px side; tighter on mobile */
  border-bottom: 1px solid #f5f7fb;
  min-width: 0;
}
.ck-sum-item:last-child { border-bottom: none; }
.ck-sum-item__img-wrap {
  position: relative;
  width: 44px;              /* FIX: was 48px */
  height: 44px;
  flex-shrink: 0;
}
.ck-sum-item__img {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid #e8ecf3;
}
.ck-sum-item__img-fallback {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background: #f0f4fa;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b8c2d8;
  border: 1px solid #e8ecf3;
}
.ck-sum-item__qty {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  background: #013d97;
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  border: 2px solid #fff;
}
.ck-sum-item__info {
  flex: 1;
  min-width: 0;
}
.ck-sum-item__name {
  font-size: 13px;
  font-weight: 700;
  color: #0f1c38;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ck-sum-item__brand {
  font-size: 10.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #029add;
  display: block;
}
.ck-sum-item__total {
  font-size: 13px;
  font-weight: 800;
  color: #013d97;
  flex-shrink: 0;
}

/* ── Coupon ── */
.ck-coupon { padding: 14px 16px; border-top: 1px solid #f0f3f8; } /* FIX: was 20px side */
.ck-coupon__row { display: flex; gap: 8px; }
.ck-coupon__input {
  flex: 1;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  padding: 9px 12px;
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  color: #0f1c38;
  background: #fafbfd;
  outline: none;
  transition: border-color 0.18s;
  letter-spacing: 0.04em;
  min-width: 0;             /* FIX: don't push button off screen */
}
.ck-coupon__input:focus { border-color: #013d97; }
.ck-coupon__btn {
  padding: 9px 12px;        /* FIX: was 14px; tighter so it fits */
  background: #013d97;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
  flex-shrink: 0;
}
.ck-coupon__btn:hover { background: #012f77; }
.ck-coupon__msg {
  font-size: 12px;
  font-weight: 600;
  margin-top: 7px;
}
.ck-coupon__msg--success { color: #16a34a; }
.ck-coupon__msg--error   { color: #dc2626; }

/* ── Totals ── */
.ck-totals { padding: 14px 16px 20px; display: flex; flex-direction: column; gap: 8px; } /* FIX: was 20px side */
.ck-totals__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13.5px;
  color: #374151;
  font-weight: 500;
  gap: 8px;
  min-width: 0;
}
.ck-totals__row--discount { color: #16a34a; }
.ck-totals__row--total {
  font-size: 17px;
  font-weight: 900;
  color: #0f1c38;
}
.ck-totals__free { color: #6b7a99; font-size: 12px; } /* FIX: was 12.5px; fits better */
.ck-totals__divider { height: 1px; background: #f0f3f8; margin: 4px 0; }
.ck-totals__vat { font-size: 11px; color: #9ca3af; }

/* ── Done page ── */
.ck-done {
  max-width: 760px;
  margin: 0 auto;
  padding: 36px 16px 80px; /* FIX: was 32px side padding */
  width: 100%;
}
.ck-done__main { display: flex; flex-direction: column; gap: 20px; }
.ck-done__actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

/* ── Accessibility ── */
.ck-page *:focus-visible {
  outline: 2px solid #029add;
  outline-offset: 2px;
}
@media (prefers-reduced-motion: reduce) {
  .ck-page *, .ck-page *::before, .ck-page *::after {
    transition-duration: 0ms !important;
    animation-duration: 0ms !important;
  }
}

/* ── Small screen overrides ── */
@media (max-width: 480px) {
  .ck-hero__inner { padding: 0 12px; }
  .ck-card { padding: 18px 14px; gap: 14px; }
  .ck-done { padding: 20px 12px 60px; }

  /* Stack Back + action button vertically */
  .ck-card__actions--row {
    flex-direction: column;
  }
  .ck-card__actions--row .ck-btn {
    width: 100%;
    justify-content: center;
  }

  /* Done page actions stack */
  .ck-done__actions {
    flex-direction: column;
  }
  .ck-done__actions .ck-btn {
    width: 100%;
    justify-content: center;
  }

  /* Payment option: hide icon on very small screens to save space */
  .ck-pay-opt__icon { display: none; }

  /* Summary padding */
  .ck-summary__title { padding: 14px 14px 12px; }
  .ck-sum-item { padding: 10px 14px; }
  .ck-coupon { padding: 12px 14px; }
  .ck-totals { padding: 12px 14px 18px; }

  /* Bank card rows: full width stacked */
  .ck-bank-card__row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  .ck-bank-card__val-wrap {
    justify-content: flex-start;
    width: 100%;
  }
  .ck-bank-card__val {
    white-space: normal;
    word-break: break-all;
  }

  /* Contact buttons: smaller text */
  .ck-contact-btn {
    font-size: 12.5px;
    padding: 11px 14px;
  }
}
`;